<template>
  <Modal
    :value="value"
    title="站内邮件"
    width="960"
    :footer-hide="true"
    @on-visible-change="handleVisibleChange"
  >
    <div class="mail-system">
      <div class="mail-sidebar">
        <div class="mail-toolbar">
          <RadioGroup v-model="filterStatus" type="button" size="small">
            <Radio label="all">全部</Radio>
            <Radio label="unread">未读</Radio>
            <Radio label="unclaimed">待领取</Radio>
          </RadioGroup>
          <Button size="small" :loading="loading" @click="loadMailList">刷新</Button>
        </div>

        <div v-if="filteredMails.length" class="mail-list">
          <div
            v-for="mail in filteredMails"
            :key="mail.id"
            class="mail-item"
            :class="{ active: selectedMail && selectedMail.id === mail.id, unread: Number(mail.status) === 0 }"
            @click="selectMail(mail)"
          >
            <div class="mail-item-head">
              <span class="mail-title">{{ mail.title }}</span>
              <Tag v-if="Number(mail.status) === 0" color="blue">未读</Tag>
              <Tag v-else-if="Number(mail.claimed) === 1" color="green">已领取</Tag>
              <Tag v-else-if="Number(mail.hasAttachment) === 1" color="orange">附件</Tag>
            </div>
            <div class="mail-item-meta">
              <span>{{ mail.senderName || '系统' }}</span>
              <span>{{ formatDate(mail.createTime) }}</span>
            </div>
            <div v-if="mail.attachPreview" class="mail-attach-preview">{{ mail.attachPreview }}</div>
          </div>
        </div>

        <div v-else class="mail-empty">暂无邮件</div>
      </div>

      <div class="mail-detail">
        <div v-if="selectedMail" class="mail-detail-inner">
          <div class="mail-detail-head">
            <div class="mail-detail-title">
              <h3>{{ selectedMail.title }}</h3>
              <div class="mail-detail-meta">
                <span>发件人：{{ selectedMail.senderName || '系统' }}</span>
                <span>时间：{{ formatDate(selectedMail.createTime) }}</span>
                <span v-if="selectedMail.expireTime">过期：{{ formatDate(selectedMail.expireTime) }}</span>
              </div>
            </div>
            <div class="mail-detail-actions">
              <Button
                v-if="Number(selectedMail.hasAttachment) === 1 && Number(selectedMail.claimed) !== 1 && !selectedMail.expired"
                type="primary"
                size="small"
                :loading="claiming"
                @click="claimAttachment"
              >
                领取附件
              </Button>
              <Button size="small" :loading="deleting" @click="deleteMail">
                删除
              </Button>
            </div>
          </div>

          <Alert v-if="selectedMail.expired" type="warning" show-icon>该邮件已过期。</Alert>
          <Alert v-else-if="Number(selectedMail.claimed) === 1" type="success" show-icon>附件已领取。</Alert>
          <Alert v-else-if="Number(selectedMail.hasAttachment) === 1" type="info" show-icon>该邮件含有可领取附件。</Alert>

    
          <div class="mail-content">{{ selectedMail.content || '无正文内容' }}</div>
            <div v-if="selectedMail.attachPreview" class="mail-attach-box">
            <div class="attach-title">附件内容</div>
            <div class="attach-content">{{ selectedMail.attachPreview }}</div>
          </div>
        </div>
        <div v-else class="mail-empty detail-empty">请选择一封邮件</div>
      </div>
    </div>
  </Modal>
</template>

<script>
export default {
  name: 'MailInboxSystem',
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      claiming: false,
      deleting: false,
      filterStatus: 'all',
      mailList: [],
      selectedMail: null
    };
  },
  computed: {
    filteredMails() {
      if (this.filterStatus === 'unread') {
        return this.mailList.filter(item => Number(item.status) === 0);
      }
      if (this.filterStatus === 'unclaimed') {
        return this.mailList.filter(item => Number(item.hasAttachment) === 1 && Number(item.claimed) !== 1 && !item.expired);
      }
      return this.mailList;
    }
  },
  methods: {
    handleVisibleChange(visible) {
      this.$emit('input', visible);
      if (visible) {
        this.loadMailList();
      }
    },
    async loadMailList() {
      this.loading = true;
      try {
        const res = await this.$http.get(`/gameMail/queryMailList?charaId=${this.getCookie('charaId')}`);
        this.mailList = (res.data && res.data.data) || [];
        if (this.selectedMail) {
          this.selectedMail = this.mailList.find(item => item.id === this.selectedMail.id) || null;
        }
        if (!this.selectedMail && this.mailList.length) {
          this.selectMail(this.mailList[0], false);
        }
        this.$emit('mail-updated');
      } catch (err) {
        this.$Message.error((err.data && err.data.msg) || '获取邮件失败');
      } finally {
        this.loading = false;
      }
    },
    async selectMail(mail, markRead = true) {
      this.selectedMail = mail;
      if (!mail || !markRead || Number(mail.status) !== 0) {
        return;
      }
      try {
        const res = await this.$http.post(`/gameMail/readMail?charaId=${this.getCookie('charaId')}&mailPlayerId=${mail.id}`);
        if (res.data && res.data.data) {
          this.selectedMail = res.data.data;
          this.replaceMail(this.selectedMail);
          this.$emit('mail-updated');
        }
      } catch (err) {
        this.$Message.warning((err.data && err.data.msg) || '读取邮件失败');
      }
    },
    async claimAttachment() {
      if (!this.selectedMail) return;
      this.claiming = true;
      try {
        await this.$http.post(`/gameMail/claimMailAttachment?charaId=${this.getCookie('charaId')}&mailPlayerId=${this.selectedMail.id}`);
        this.$Message.success('领取成功');
        await this.loadMailList();
      } catch (err) {
        this.$Message.warning((err.data && err.data.msg) || '领取失败');
      } finally {
        this.claiming = false;
      }
    },
    async deleteMail() {
      if (!this.selectedMail) return;
      this.deleting = true;
      try {
        const targetId = this.selectedMail.id;
        await this.$http.post(`/gameMail/deleteMail?charaId=${this.getCookie('charaId')}&mailPlayerId=${targetId}`);
        this.$Message.success('删除成功');
        this.mailList = this.mailList.filter(item => item.id !== targetId);
        this.selectedMail = this.mailList.length ? this.mailList[0] : null;
        this.$emit('mail-updated');
      } catch (err) {
        this.$Message.warning((err.data && err.data.msg) || '删除失败');
      } finally {
        this.deleting = false;
      }
    },
    replaceMail(mail) {
      const index = this.mailList.findIndex(item => item.id === mail.id);
      if (index !== -1) {
        this.$set(this.mailList, index, mail);
      }
    },
    formatDate(value) {
      if (!value) return '--';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      const pad = num => `${num}`.padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }
  }
};
</script>

<style scoped>
.mail-system {
  display: flex;
  gap: 16px;
  min-height: 560px;
}

.mail-sidebar {
  width: 320px;
  border-right: 1px solid #e8eaec;
  padding-right: 16px;
}

.mail-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.mail-list {
  max-height: 500px;
  overflow-y: auto;
}

.mail-item {
  padding: 12px;
  border: 1px solid #e8eaec;
  border-radius: 8px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mail-item:hover,
.mail-item.active {
  border-color: #2d8cf0;
  background: #f0f7ff;
}

.mail-item.unread {
  border-left: 4px solid #2d8cf0;
}

.mail-item-head {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.mail-title {
  font-weight: 600;
  color: #17233d;
}

.mail-item-meta,
.mail-detail-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  color: #808695;
  font-size: 12px;
  margin-top: 8px;
}

.mail-attach-preview {
  margin-top: 8px;
  font-size: 12px;
  color: #2d8cf0;
  line-height: 1.6;
}

.mail-detail {
  flex: 1;
  min-width: 0;
}

.mail-detail-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.mail-detail-title h3 {
  margin: 0;
  color: #17233d;
}

.mail-detail-actions {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.mail-attach-box {
  background: #f8f9fa;
  border: 1px dashed #2d8cf0;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 12px;
}

.attach-title {
  font-weight: 600;
  color: #17233d;
  margin-bottom: 6px;
}

.attach-content {
  color: #2d8cf0;
  line-height: 1.8;
  word-break: break-all;
}

.mail-content {
  min-height: 380px;
  background: #f8f9fa;
  border: 1px solid #e8eaec;
  border-radius: 8px;
  padding: 16px;
  white-space: pre-wrap;
  line-height: 1.9;
  color: #515a6e;
}

.mail-empty {
  color: #999;
  text-align: center;
  padding: 24px 12px;
}

.detail-empty {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 420px;
  background: #f8f9fa;
  border-radius: 8px;
}

@media (max-width: 768px) {
  .mail-system {
    flex-direction: column;
    min-height: auto;
  }

  .mail-sidebar {
    width: 100%;
    border-right: 0;
    border-bottom: 1px solid #e8eaec;
    padding-right: 0;
    padding-bottom: 12px;
  }

  .mail-list {
    max-height: 220px;
  }

  .mail-detail-head {
    flex-direction: column;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/MailInboxSystem.vue